import {useEffect} from 'react';
import {ELEMENT_NOT_FOUND} from 'app/utils/Consts';

const pageClass = 'affiliates-page';

const markPage = (): void => {
    document.documentElement.classList.add(pageClass);
    document.body.classList.add(pageClass);
};

const unmarkPage = (): void => {
    document.documentElement.classList.remove(pageClass);
    document.body.classList.remove(pageClass);
};

const bindReveal = (): (() => void) | null => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return null;
    }

    const nodes = document.querySelectorAll('.pcard.rv, .wcard.rv, .step.rv');
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.remove('pre');
                observer.unobserve(entry.target);
            }
        });
    });

    nodes.forEach(node => {
        if (node.getBoundingClientRect().top > window.innerHeight) {
            node.classList.add('pre');
            observer.observe(node);
        }
    });

    return () => observer.disconnect();
};

const syncMobileBar = (
    bar: HTMLElement,
    hero: HTMLElement,
    visible: Record<string, boolean>
): void => {
    const pastHero = hero.getBoundingClientRect().bottom < 0;
    const show = pastHero && !visible.heroBtn && !visible.ctaBtn && !visible.foot;
    const link = bar.querySelector('a');

    bar.classList.toggle('show', show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');

    if (link instanceof HTMLAnchorElement) {
        link.tabIndex = show ? 0 : ELEMENT_NOT_FOUND;
    }
};

const bindMobileBar = (): (() => void) | null => {
    const bar = document.getElementById('mbar');
    const hero = document.getElementById('heroBtn');
    const cta = document.getElementById('ctaBtn');
    const foot = document.querySelector('.ftr');

    if (!bar || !hero || !cta || !(foot instanceof HTMLElement)) {
        return null;
    }

    foot.id = 'foot';

    const visible: Record<string, boolean> = {};
    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            visible[entry.target.id] = entry.isIntersecting;
        });
        syncMobileBar(bar, hero, visible);
    });

    observer.observe(hero);
    observer.observe(cta);
    observer.observe(foot);

    return () => observer.disconnect();
};

export const useAffiliatesPage = (): void => {
    useEffect(() => {
        markPage();

        const stopReveal = bindReveal();
        const stopBar = bindMobileBar();

        return () => {
            unmarkPage();
            if (stopReveal) {
                stopReveal();
            }
            if (stopBar) {
                stopBar();
            }
        };
    }, []);
};
